// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import './styles/main.css';
import { EventLog } from './eventlog.ts';
import { initGlossary } from './glossary.ts';
import { createDrawer, createModalHost, el, formatBytes, formatCount, toast } from './ui/shell.ts';
import {
  fromDirectoryHandle,
  fromEntries,
  fromFileList,
  fromZip,
  totalBytes,
  type IngestResult,
} from './io/ingest.ts';
import { buildGroups, hashLinks } from './core/grouping.ts';
import type { EmbeddingMatrix } from './core/vectors.ts';
import { findSimilarPairs, describeCosine, type SimilarPair } from './core/similar.ts';
import { DEFAULT_THRESHOLDS, type Decision, type Group, type ImageRecord, type Thresholds } from './core/types.ts';
import { Pipeline } from './pipeline.ts';
import { CSV_BOM, IMAGES_DIR, reportCsv, planExport } from './core/dataset.ts';
import { conceptDirName } from './core/naming.ts';
import { exportEntries, summarise } from './export.ts';
import { ZipTooBigError, fileSystemSink, memorySink, writeZip } from './io/zipwriter.ts';
import { MODEL_BYTES, MODEL_ID } from './model/config.mjs';

const $ = <T extends HTMLElement>(id: string): T => document.getElementById(id) as T;

const log = new EventLog($<HTMLOListElement>('drawer-list'));
const modal = createModalHost($('modal-host'));
const drawer = createDrawer($('drawer'), $<HTMLButtonElement>('log-toggle'), $<HTMLButtonElement>('log-close'));
initGlossary($('tooltip'));

/* ── State ─────────────────────────────────────────────────────────────────── */

interface Analysis {
  readonly records: readonly ImageRecord[];
  readonly matrix: EmbeddingMatrix | null;
  readonly files: Map<number, File>;
}

let ingest: IngestResult | null = null;
let analysis: Analysis | null = null;
let thresholds: Thresholds = { ...DEFAULT_THRESHOLDS };
let groups: readonly Group[] = [];
/**
 * The pristine output of `buildGroups`, before any keeper override or manual set-aside.
 *
 * Kept separately because every re-render has to start from it. Feeding the PREVIOUS rebuild back
 * in makes the overrides one-way: an image already marked `excluded` no longer matches the
 * `verdict === 'kept'` branch, so clicking a set-aside image to bring it back did nothing.
 */
let baseDecisions: readonly Decision[] = [];
let decisions: readonly Decision[] = [];
let kept: number[] = [];
/** Keeper overrides, group id -> image id. Cleared whenever the grouping is recomputed. */
const keeperOverride = new Map<number, number>();
/**
 * Images the user set aside by hand from the look-alike list.
 *
 * A separate set from the grouping, and deliberately so: the embedding is never allowed to remove
 * anything on its own, so the only way one of its suggestions takes effect is a person clicking it.
 */
const manuallyDropped = new Set<number>();
let similar: { pairs: SimilarPair[]; total: number } = { pairs: [], total: 0 };
let filter: 'groups' | 'dropped' | 'all' = 'groups';
let pipeline: Pipeline | null = null;
/** Object URLs for the review thumbnails, revoked when the grid is rebuilt or the run resets. */
let thumbUrls: string[] = [];

/* ── Theme ─────────────────────────────────────────────────────────────────── */

const THEME_KEY = 'loraprep.theme';
function applyTheme(theme: 'dark' | 'light'): void {
  document.documentElement.dataset['theme'] = theme;
  $('theme-toggle').setAttribute('aria-label', `Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`);
}
applyTheme((localStorage.getItem(THEME_KEY) as 'dark' | 'light' | null) ?? 'dark');
$('theme-toggle').addEventListener('click', () => {
  const next = document.documentElement.dataset['theme'] === 'light' ? 'dark' : 'light';
  localStorage.setItem(THEME_KEY, next);
  applyTheme(next);
});

/* ── Modals ────────────────────────────────────────────────────────────────── */

const MODAL_TITLES: Record<string, string> = {
  how: 'How it works',
  privacy: 'Privacy & threat model',
  limits: 'Limits — read these',
  about: 'About Loraprep',
  keys: 'Keyboard shortcuts',
};

function openModal(name: string): void {
  const tpl = document.getElementById(`tpl-${name}`) as HTMLTemplateElement | null;
  if (!tpl) return;
  modal.show(MODAL_TITLES[name] ?? name, tpl.content.cloneNode(true));
}

document.addEventListener('click', (e) => {
  const btn = (e.target as HTMLElement).closest<HTMLElement>('[data-modal]');
  if (btn) openModal(btn.dataset['modal']!);
});

/* ── Panels ────────────────────────────────────────────────────────────────── */

type Panel = 'input' | 'preflight' | 'progress' | 'review' | 'export';
function show(panel: Panel): void {
  for (const p of ['input', 'preflight', 'progress', 'review', 'export'] as Panel[]) {
    $(`${p}-panel`).toggleAttribute('hidden', p !== panel);
  }
  $('error').setAttribute('hidden', '');
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

function fail(message: string): void {
  $('error-text').textContent = message;
  $('error').removeAttribute('hidden');
  log.emit('error', message);
}

/* ── Capability strip ──────────────────────────────────────────────────────── */

function capabilityStrip(): void {
  const hasPicker = 'showDirectoryPicker' in window;
  const items: [string, string][] = [
    ['Runs on', 'your machine'],
    ['Model', `${(MODEL_BYTES / 1048576).toFixed(0)} MB, once`],
    ['Uploads', 'none'],
    ['Folder picker', hasPicker ? 'available' : 'drag or zip'],
  ];
  $('capability-strip').replaceChildren(
    ...items.map(([k, v]) => el('li', {}, [`${k}: `, el('strong', {}, [v])])),
  );
  if (hasPicker) $('pick-folder').removeAttribute('hidden');
}
capabilityStrip();

/* ── Input ─────────────────────────────────────────────────────────────────── */

const dropzone = $('dropzone');

function revokeThumbs(): void {
  for (const u of thumbUrls) URL.revokeObjectURL(u);
  thumbUrls = [];
}

async function accept(result: IngestResult, label: string): Promise<void> {
  revokeThumbs();
  if (result.files.length === 0) {
    const extra = result.skippedHeic.length > 0 ? ` ${result.skippedHeic.length} HEIC file(s) were found — no browser can decode those.` : '';
    fail(`No readable images in ${label}.${extra}`);
    return;
  }
  ingest = result;
  analysis = null;
  log.emit('good', `${formatCount(result.files.length)} image(s) from ${label}, ${formatBytes(totalBytes(result.files))}.`);
  if (result.skippedHeic.length > 0) {
    log.emit('warn', `${result.skippedHeic.length} HEIC/HEIF file(s) skipped — no browser decodes them. Convert first.`);
  }
  if (result.skippedOther.length > 0) {
    log.emit('info', `${result.skippedOther.length} non-image file(s) ignored.`);
  }
  preflight();
  show('preflight');
}

function preflight(): void {
  if (!ingest) return;
  const bytes = totalBytes(ingest.files);
  const n = ingest.files.length;

  const facts: [string, string, string?][] = [
    ['Images', formatCount(n)],
    ['Total size', formatBytes(bytes)],
  ];
  if (ingest.skippedHeic.length > 0) {
    facts.push(['HEIC skipped', formatCount(ingest.skippedHeic.length), 'No browser can decode HEIC — convert them first.']);
  }
  if (n > 2000) {
    facts.push(['Over the stated ceiling', `${formatCount(n)} > 2,000`, 'It will still run. Decoding this many full-resolution photos is the likeliest way to have a phone discard the tab.']);
  }

  $('preflight-facts').replaceChildren(
    el(
      'dl',
      { class: 'stats' },
      facts.map(([k, v, note]) =>
        el('div', {}, [el('dt', {}, [k]), el('dd', {}, [v]), ...(note ? [el('p', { class: 'field-note' }, [note])] : [])]),
      ),
    ),
  );

  $('embed-note').textContent =
    `Downloads a ${(MODEL_BYTES / 1048576).toFixed(0)} MB vision model once, then runs it here. It does not remove ` +
    `anything — it ranks pairs that look alike, for you to go through. ` +
    estimate(ingest.files.length, bytes, true);
  $('hash-note').textContent = estimate(ingest.files.length, bytes, false);
}

/**
 * An honest pre-run estimate — a RANGE, with its provenance, replaced by measurement as soon as
 * there is any.
 *
 * Every number here was measured on an M3 Pro, which is roughly the fastest consumer laptop
 * available; the range's upper end is what a mid-range machine does. Decoding a 12-megapixel
 * phone photograph takes 180-210 ms, not the 20-50 ms that gets quoted — and embedding adds about
 * 140 ms on top. Average file size is the only proxy for resolution available before anything has
 * been decoded, so the estimate scales on it and is deliberately wide until the live rate lands.
 */
function estimate(n: number, bytes: number, withModel: boolean): string {
  const avgMB = bytes / n / 1_048_576;
  // ~2 MB is a 12-megapixel phone JPEG at 180 ms; scale from there, with a floor for small images.
  const decodeMs = Math.max(25, 90 * Math.min(4, Math.max(0.35, avgMB / 2)));
  const perImage = decodeMs + (withModel ? 145 : 0);
  const lo = (n * perImage) / 1000;
  const hi = lo * 2.6;
  const fmt = (s: number): string =>
    s < 90 ? `${Math.max(1, Math.round(s))} seconds` : s < 5400 ? `${Math.round(s / 60)} minutes` : `${(s / 3600).toFixed(1)} hours`;
  return (
    `Expect roughly ${fmt(lo)} to ${fmt(hi)} for ${formatCount(n)} images — the fast end is a recent laptop, ` +
    `the slow end a phone or an older machine. The real rate is shown once it has done twenty.`
  );
}

dropzone.addEventListener('dragover', (e) => {
  e.preventDefault();
  dropzone.classList.add('is-dragover');
});
dropzone.addEventListener('dragleave', () => dropzone.classList.remove('is-dragover'));
dropzone.addEventListener('drop', (e) => {
  e.preventDefault();
  dropzone.classList.remove('is-dragover');
  const dt = e.dataTransfer;
  if (!dt) return;
  // webkitGetAsEntry MUST be called synchronously — the DataTransfer items are neutered the
  // instant this handler yields, so the entries are collected before any await.
  const entries: FileSystemEntry[] = [];
  for (const item of Array.from(dt.items)) {
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  const files = Array.from(dt.files);
  void (async () => {
    try {
      if (files.length === 1 && /\.zip$/i.test(files[0]!.name)) {
        await accept(await fromZip(files[0]!), files[0]!.name);
      } else if (entries.length > 0) {
        await accept(await fromEntries(entries), entries.length === 1 ? entries[0]!.name : 'the dropped items');
      } else if (files.length > 0) {
        await accept(fromFileList(files), 'the dropped files');
      }
    } catch (err) {
      fail(err instanceof Error ? err.message : String(err));
    }
  })();
});

$('pick-files').addEventListener('click', () => $<HTMLInputElement>('file-input').click());
$<HTMLInputElement>('file-input').addEventListener('change', (e) => {
  const input = e.target as HTMLInputElement;
  const list = Array.from(input.files ?? []);
  input.value = '';
  if (list.length === 0) return;
  void (async () => {
    try {
      if (list.length === 1 && /\.zip$/i.test(list[0]!.name)) await accept(await fromZip(list[0]!), list[0]!.name);
      else await accept(fromFileList(list), 'the chosen files');
    } catch (err) {
      fail(err instanceof Error ? err.message : String(err));
    }
  })();
});

$('pick-folder').addEventListener('click', () => {
  void (async () => {
    try {
      const dir = await (window as Window & { showDirectoryPicker?: () => Promise<FileSystemDirectoryHandle> })
        .showDirectoryPicker!();
      await accept(await fromDirectoryHandle(dir), dir.name);
    } catch (err) {
      // An abort is the user changing their mind, not a failure.
      if (err instanceof DOMException && err.name === 'AbortError') return;
      fail(err instanceof Error ? err.message : String(err));
    }
  })();
});

$('try-sample').addEventListener('click', (e) => {
  // The sample control sits inside the drop zone; without this it also opens the file picker.
  e.stopPropagation();
  void (async () => {
    try {
      log.emit('info', 'Fetching the bundled sample…');
      const res = await fetch(`${import.meta.env.BASE_URL}samples/lora-set.zip`);
      if (!res.ok) throw new Error(`sample fetch failed: HTTP ${res.status}`);
      // Straight through the same ingestion path a dropped ZIP takes — never a separate branch.
      await accept(await fromZip(await res.blob()), 'the sample set (36 synthetic images)');
    } catch (err) {
      fail(err instanceof Error ? err.message : String(err));
    }
  })();
});

$('run-back').addEventListener('click', () => show('input'));

/* ── Run ───────────────────────────────────────────────────────────────────── */

$('run-start').addEventListener('click', () => {
  if (!ingest) return;
  const useEmbeddings = $<HTMLInputElement>('opt-embed').checked;
  const source = ingest;
  show('progress');
  $('progress-bar').style.width = '0%';
  $('progress-meta').textContent = 'Starting…';
  log.emit(
    'info',
    `Analysing ${formatCount(source.files.length)} image(s)${useEmbeddings ? ' with the vision model' : ' with the perceptual hash only'}.`,
  );

  pipeline?.terminate();
  pipeline = new Pipeline();
  void pipeline
    .run({
      paths: source.files.map((f) => f.path),
      files: source.files.map((f) => f.file),
      useEmbeddings,
      // Resolved on the MAIN thread from the document base. Inside a worker,
      // `import.meta.env.BASE_URL` resolves against the worker's own /assets/ location and ONNX
      // Runtime then fails with "no available backend found".
      ortBase: new URL('ort/', document.baseURI).href,
      onLog: (level, message) => log.emit(level, message),
      onModelProgress: (loaded, total) => {
        const pct = total > 0 ? (loaded / total) * 100 : 0;
        $('progress-bar').style.width = `${pct.toFixed(1)}%`;
        $('progress-meta').textContent = `downloading the model — ${formatBytes(loaded)} of ${formatBytes(total)}`;
      },
      onBackend: (report) => {
        log.emit(
          'good',
          `Model ready on ${report.device} (${report.dtype}), ${report.threads} thread${report.threads === 1 ? '' : 's'}. ` +
            `Self-check passed at cosine ${report.probeCosine.toFixed(5)}.`,
        );
        for (const r of report.rejected) log.emit('warn', `Backend rejected — ${r}`);
      },
      onProgress: (done, total, rate) => {
        const pct = total > 0 ? (done / total) * 100 : 0;
        $('progress-bar').style.width = `${pct.toFixed(1)}%`;
        const parts = [`${formatCount(done)} of ${formatCount(total)} images`];
        if (rate > 0) {
          parts.push(`${rate.toFixed(1)} images/s`);
          const left = (total - done) / rate;
          parts.push(left < 60 ? `about ${Math.max(1, Math.round(left))}s left` : `about ${Math.round(left / 60)} min left`);
        } else {
          parts.push('measuring speed…');
        }
        $('progress-meta').textContent = parts.join(' · ');
      },
    })
    .then((result) => {
      if (result.cancelled) return;
      const files = new Map<number, File>();
      const byPath = new Map(source.files.map((f) => [f.path, f.file]));
      for (const r of result.records) {
        const f = byPath.get(r.path);
        if (f) files.set(r.id, f);
      }
      analysis = { records: result.records, matrix: result.matrix, files };
      log.emit(
        'good',
        `Analysed ${formatCount(result.records.length)} image(s) in ${(result.elapsedMs / 1000).toFixed(1)}s` +
          `${result.failed.length > 0 ? `, ${result.failed.length} unreadable` : ''}.`,
      );
      $<HTMLElement>('similar-section').toggleAttribute('hidden', result.matrix === null);
      keeperOverride.clear();
      manuallyDropped.clear();
      recompute();
      show('review');
    })
    .catch((err: unknown) => {
      fail(err instanceof Error ? err.message : String(err));
      show('preflight');
    });
});

$('run-cancel').addEventListener('click', () => {
  pipeline?.cancel();
  pipeline?.terminate();
  log.emit('warn', 'Cancelled.');
  show('preflight');
});

/* ── Review ────────────────────────────────────────────────────────────────── */

function recompute(): void {
  if (!analysis) return;
  // HASH LINKS ONLY. The embedding is never allowed into this graph — see src/core/similar.ts for
  // the measurements. In short: on hand-curated reference sets, genuinely different photographs of
  // one subject reach cosine 0.99 while a mirrored duplicate scores 0.88, so there is no cutoff
  // that removes duplicates without removing the dataset, and union-find over those edges chained
  // six distinct photographs of one bowl into a single cluster.
  const links = hashLinks(analysis.records, thresholds.hamming);
  const out = buildGroups(analysis.records, links, thresholds);
  groups = applyOverrides(out.groups);
  baseDecisions = out.decisions;
  const rebuilt = rebuildDecisions(baseDecisions);
  decisions = rebuilt.decisions;
  kept = rebuilt.kept;

  if (analysis.matrix) {
    const groupOf = new Map<number, number>();
    for (const g of groups) for (const m of g.members) groupOf.set(m, g.id);
    similar = findSimilarPairs(
      analysis.records,
      analysis.matrix,
      thresholds.cosine,
      120,
      (a, b) => groupOf.get(a) === groupOf.get(b),
    );
  } else {
    similar = { pairs: [], total: 0 };
  }

  renderReview();
}

/**
 * Re-point a group at a keeper the user chose.
 *
 * Done as a post-pass rather than by threading a preference into `buildGroups`, so the grouping
 * stays a pure function of the thresholds and the choice survives a slider move as long as the
 * group still contains that image.
 */
function applyOverrides(input: readonly Group[]): Group[] {
  return input.map((g) => {
    const wanted = keeperOverride.get(g.id);
    return wanted !== undefined && g.members.includes(wanted) ? { ...g, keeper: wanted } : g;
  });
}

function rebuildDecisions(base: readonly Decision[]): { decisions: Decision[]; kept: number[] } {
  const byId = new Map(analysis!.records.map((r) => [r.id, r]));
  const out: Decision[] = [];
  const keptIds: number[] = [];
  const groupById = new Map(groups.map((g) => [g.id, g]));
  for (const d of base) {
    const g = d.groupId === null ? null : groupById.get(d.groupId);
    if (!g || g.members.length === 1 || keeperOverride.get(g.id) === undefined) {
      out.push(d);
      if (d.verdict === 'kept') keptIds.push(d.id);
      continue;
    }
    const keeper = byId.get(g.keeper)!;
    if (d.id === g.keeper) {
      out.push({ id: d.id, verdict: 'kept', reason: 'kept — you chose this one', against: null, groupId: g.id });
      keptIds.push(d.id);
    } else if (d.verdict === 'excluded') {
      out.push(d);
    } else {
      out.push({
        id: d.id,
        verdict: 'duplicate',
        reason: `near-duplicate of ${keeper.path}`,
        against: keeper.id,
        groupId: g.id,
      });
    }
  }
  // Manual set-asides are applied last, over everything the grouping decided. They are the ONLY
  // way an embedding suggestion can remove an image, and they survive a slider move.
  const finalDecisions = out.map((d) =>
    manuallyDropped.has(d.id) && d.verdict === 'kept'
      ? { id: d.id, verdict: 'excluded' as const, reason: 'you set this one aside', against: null, groupId: d.groupId }
      : d,
  );
  const finalKept = keptIds.filter((id) => !manuallyDropped.has(id));
  finalKept.sort((a, b) => a - b);
  return { decisions: finalDecisions, kept: finalKept };
}

function renderReview(): void {
  if (!analysis) return;
  const total = analysis.records.length;
  const dropped = total - kept.length;
  const multi = groups.filter((g) => g.members.length > 1);

  $('headline').replaceChildren(
    el('span', { class: `big ${dropped > 0 ? 'is-good' : ''}` }, [formatCount(kept.length)]),
    el('span', {}, [
      ` of ${formatCount(total)} images kept — ${formatCount(dropped)} set aside across ${formatCount(multi.length)} group${multi.length === 1 ? '' : 's'}.`,
    ]),
  );

  const stat = (k: string, v: string): HTMLElement => el('div', {}, [el('dt', {}, [k]), el('dd', {}, [v])]);
  const biggest = multi.reduce((n, g) => Math.max(n, g.members.length), 0);
  $('stats').replaceChildren(
    stat('Kept', formatCount(kept.length)),
    stat('Set aside', formatCount(dropped)),
    stat('Groups', formatCount(multi.length)),
    stat('Largest group', biggest > 0 ? `${biggest} frames` : '—'),
  );

  $('review-summary').textContent =
    filter === 'groups'
      ? `${formatCount(multi.length)} group(s) with more than one frame`
      : filter === 'dropped'
        ? `${formatCount(dropped)} image(s) set aside`
        : `${formatCount(groups.length)} group(s) in total`;

  renderGrid();
  renderHammingNote();
  renderCosineNote();
  renderSimilar();
}

/**
 * The look-alike list: pairs, ranked, with NOTHING pre-selected.
 *
 * Rendered as pairs rather than clusters on purpose. Clustering these scores is what destroys a
 * dataset — chaining merged six distinct photographs of one subject through a link scoring 0.809,
 * nine hundredths below the threshold that formed the cluster. A pair is a claim the user can
 * check with their own eyes in one glance; a cluster is a claim they have to take on trust.
 */
function renderSimilar(): void {
  if (!analysis) return;
  const host = $('similar-list');
  const byId = new Map(analysis.records.map((r) => [r.id, r]));

  $('similar-summary').textContent =
    similar.total === 0
      ? 'Nothing else looked alike at this setting.'
      : `${formatCount(similar.total)} pair(s) above this similarity` +
        (similar.total > similar.pairs.length ? ` — showing the ${similar.pairs.length} closest` : '');

  if (similar.pairs.length === 0) {
    host.replaceChildren();
    return;
  }

  host.replaceChildren(
    ...similar.pairs.map((p) => {
      const a = byId.get(p.a)!;
      const b = byId.get(p.b)!;
      const tile = (rec: ImageRecord): HTMLElement => {
        const file = analysis!.files.get(rec.id);
        const url = file ? URL.createObjectURL(file) : '';
        if (url) thumbUrls.push(url);
        const dropped = manuallyDropped.has(rec.id) || !kept.includes(rec.id);
        const btn = el(
          'button',
          {
            type: 'button',
            class: `tile ${dropped ? 'is-dropped' : ''}`,
            title: `${rec.path}\n${dropped ? 'set aside' : 'click to set aside'}`,
          },
          [
            el('img', { src: url, alt: rec.path, loading: 'lazy', decoding: 'async' }),
            el('span', { class: 'tile-badge' }, [dropped ? 'set aside' : 'keeping']),
            el('span', { class: 'tile-name' }, [rec.name]),
          ],
        );
        btn.addEventListener('click', () => {
          if (manuallyDropped.has(rec.id)) manuallyDropped.delete(rec.id);
          else manuallyDropped.add(rec.id);
          log.emit('info', `${manuallyDropped.has(rec.id) ? 'Set aside' : 'Restored'} ${rec.name}.`);
          const rebuilt = rebuildDecisions(baseDecisions);
          decisions = rebuilt.decisions;
          kept = rebuilt.kept;
          renderReview();
        });
        return btn;
      };
      return el('div', { class: 'pair' }, [
        el('div', { class: 'pair-score' }, [
          el('strong', {}, [p.cosine.toFixed(3)]),
          el('span', {}, [describeCosine(p.cosine)]),
          el('span', { class: 'field-note' }, [
            p.hamming <= 12
              ? `The fingerprint also puts these ${p.hamming} bits apart.`
              : `The fingerprint says these are ${p.hamming} bits apart — as far apart as unrelated pictures.`,
          ]),
        ]),
        el('div', { class: 'tiles' }, [tile(a), tile(b)]),
      ]);
    }),
  );
}

function renderHammingNote(): void {
  const n = thresholds.hamming;
  // The expected number of chance collisions at this image count, from the binomial tail — the
  // one number that tells a user whether the slider has stopped being useful.
  const expected = expectedCollisions(n, analysis?.records.length ?? 0);
  const note =
    n <= 2
      ? 'Only re-encodes and exact copies. A JPEG saved again at quality 40 is 1 bit.'
      : n <= 5
        ? 'The measured-safe setting: on a 158-image library of genuinely distinct photographs, 5 bits produced zero false matches across all 12,403 pairs.'
        : n <= 8
          ? 'Catches aggressive thumbnails too — a downscale to a tenth of the size is 7 bits.'
          : n <= 12
            ? 'Loose. Light crops start to be caught, and so do a few unrelated pairs.'
            : `Very loose — expect roughly ${expected.toFixed(0)} unrelated pairs to collide by chance at this many images.`;
  $('note-hamming').textContent = note;
  $('note-hamming').classList.toggle('is-warn', n > 10);
}

/** Binomial tail: P(distance <= d) for 64 independent bits, times the number of pairs. */
function expectedCollisions(d: number, n: number): number {
  if (n < 2) return 0;
  let p = 0;
  let c = 1;
  for (let k = 0; k <= d; k++) {
    if (k > 0) c = (c * (64 - k + 1)) / k;
    p += c;
  }
  return (p / 2 ** 64) * ((n * (n - 1)) / 2);
}

function renderCosineNote(): void {
  const c = thresholds.cosine;
  $('note-cosine').textContent = describeCosine(c);
  $('note-cosine').classList.toggle('is-warn', c < 0.9);
}

function renderGrid(): void {
  if (!analysis) return;
  revokeThumbs();
  const byId = new Map(analysis.records.map((r) => [r.id, r]));
  const verdictById = new Map(decisions.map((d) => [d.id, d]));

  const visible = groups.filter((g) => {
    if (filter === 'all') return true;
    if (filter === 'groups') return g.members.length > 1;
    return g.members.some((m) => verdictById.get(m)?.verdict !== 'kept');
  });

  const host = $('group-list');
  if (visible.length === 0) {
    host.replaceChildren(
      el('p', { class: 'panel-note' }, [
        filter === 'groups'
          ? 'No image matched another one at these settings. Loosen the slider, or take the whole set through as-is.'
          : 'Nothing was set aside.',
      ]),
    );
    return;
  }

  // A hard cap on rendered groups. Every group is still in the export and in the CSV; this is
  // purely about not asking a browser to lay out 4,000 thumbnails, and it SAYS it is doing it
  // rather than quietly showing the first 300.
  const CAP = 300;
  const shown = visible.slice(0, CAP);

  const nodes: Node[] = shown.map((g) => {
    const tiles = g.members.map((id) => {
      const rec = byId.get(id)!;
      const file = analysis!.files.get(id);
      const d = verdictById.get(id);
      const isKeeper = id === g.keeper && d?.verdict === 'kept';
      const url = file ? URL.createObjectURL(file) : '';
      if (url) thumbUrls.push(url);

      const img = el('img', { src: url, alt: rec.path, loading: 'lazy', decoding: 'async' });
      const tile = el(
        'button',
        {
          type: 'button',
          class: `tile ${isKeeper ? 'is-keeper' : d?.verdict === 'kept' ? 'is-kept' : 'is-dropped'}`,
          title: `${rec.path}\n${d?.reason ?? ''}`,
        },
        [
          img,
          el('span', { class: 'tile-badge' }, [isKeeper ? 'keeper' : (d?.verdict ?? '')]),
          el('span', { class: 'tile-name' }, [rec.name]),
          el('span', { class: 'tile-meta' }, [
            `${rec.width || '?'}×${rec.height || '?'} · ${formatBytes(rec.bytes)} · sharpness ${rec.sharpness.toFixed(0)}`,
          ]),
        ],
      );
      tile.addEventListener('click', () => {
        if (g.members.length === 1) return;
        keeperOverride.set(g.id, id);
        log.emit('info', `Keeping ${rec.name} from group ${g.id + 1}.`);
        groups = applyOverrides(groups);
        const rebuilt = rebuildDecisions(baseDecisions);
        decisions = rebuilt.decisions;
        kept = rebuilt.kept;
        renderReview();
      });
      return tile;
    });

    return el('section', { class: 'group' }, [
      el('h3', { class: 'group-head' }, [
        `Group ${g.id + 1}`,
        el('span', { class: 'group-meta' }, [
          `${g.members.length} frame${g.members.length === 1 ? '' : 's'} · matched by ${g.via === 'both' ? 'fingerprint and content' : g.via === 'embedding' ? 'content' : 'fingerprint'}`,
        ]),
      ]),
      el('div', { class: 'tiles' }, tiles),
    ]);
  });

  if (visible.length > CAP) {
    nodes.push(
      el('p', { class: 'panel-note' }, [
        `Showing the first ${CAP} of ${formatCount(visible.length)} groups. The rest are still in the export and in dedup-report.csv — they are just not drawn here.`,
      ]),
    );
  }
  host.replaceChildren(...nodes);
}

$<HTMLInputElement>('sl-hamming').addEventListener('input', (e) => {
  thresholds = { ...thresholds, hamming: Number((e.target as HTMLInputElement).value) };
  $('out-hamming').textContent = String(thresholds.hamming);
  keeperOverride.clear();
  recompute();
});
$<HTMLInputElement>('sl-cosine').addEventListener('input', (e) => {
  thresholds = { ...thresholds, cosine: Number((e.target as HTMLInputElement).value) };
  $('out-cosine').textContent = thresholds.cosine.toFixed(3);
  // Deliberately does NOT clear the keeper overrides or the manual set-asides: this slider only
  // changes which pairs are OFFERED for review, never what is kept.
  recompute();
});
$<HTMLInputElement>('opt-flat').addEventListener('change', (e) => {
  thresholds = { ...thresholds, excludeFlat: (e.target as HTMLInputElement).checked };
  recompute();
});

for (const btn of Array.from(document.querySelectorAll<HTMLButtonElement>('.seg-btn'))) {
  btn.addEventListener('click', () => {
    filter = btn.dataset['filter'] as typeof filter;
    for (const b of Array.from(document.querySelectorAll('.seg-btn'))) b.classList.toggle('is-on', b === btn);
    renderReview();
  });
}

$('start-over').addEventListener('click', () => {
  revokeThumbs();
  pipeline?.terminate();
  ingest = null;
  analysis = null;
  manuallyDropped.clear();
  keeperOverride.clear();
  show('input');
});
$('back-review').addEventListener('click', () => show('review'));
$('go-export').addEventListener('click', () => {
  renderTree();
  show('export');
});

/* ── Export ────────────────────────────────────────────────────────────────── */

function datasetOptions(): { repeats: number; concept: string; caption: string } {
  return {
    repeats: Number($<HTMLInputElement>('opt-repeats').value) || 1,
    concept: $<HTMLInputElement>('opt-concept').value,
    caption: $<HTMLInputElement>('opt-caption').value,
  };
}

function renderTree(): void {
  if (!analysis) return;
  const opts = datasetOptions();
  const dir = conceptDirName(opts.repeats, opts.concept);
  const plan = planExport(
    kept.map((id) => analysis!.records.find((r) => r.id === id)!),
    opts,
  );
  const first = plan.names.get(kept[0]!) ?? 'photo.jpg';
  const stem = first.includes('.') ? first.slice(0, first.lastIndexOf('.')) : first;
  const withEmb = $<HTMLInputElement>('opt-emb-file').checked && analysis.matrix !== null;

  const lines = [
    'README.md',
    'dataset.toml',
    `${IMAGES_DIR}/${dir}/`,
    `  ${first}`,
    `  ${stem}.txt`,
    `  … ${formatCount(kept.length)} image(s) and their captions`,
    `  metadata.jsonl`,
    'dedup-report.csv',
    ...(withEmb ? ['embeddings.f32', 'embeddings.json'] : []),
  ];
  $('tree-preview').replaceChildren(el('pre', {}, [lines.join('\n')]));

  if (plan.unsupported.length > 0) {
    $('tree-preview').append(
      el('p', { class: 'field-note is-warn' }, [
        `${plan.unsupported.length} kept image(s) use a format kohya does not glob by default (it reads .png .jpg .jpeg .webp .bmp). They are still included — convert them before training.`,
      ]),
    );
  }
}

for (const id of ['opt-repeats', 'opt-concept', 'opt-caption', 'opt-emb-file']) {
  $(id).addEventListener('input', renderTree);
  $(id).addEventListener('change', renderTree);
}

function exportInputs(): Parameters<typeof summarise>[0] {
  return {
    records: analysis!.records,
    decisions,
    groups,
    kept,
    files: analysis!.files,
    options: datasetOptions(),
    embeddings: analysis!.matrix ? new Float32Array(analysis!.matrix.view()) : null,
    includeEmbeddings: $<HTMLInputElement>('opt-emb-file').checked,
  };
}

$('dl-zip').addEventListener('click', () => {
  void (async () => {
    if (!analysis) return;
    const btn = $<HTMLButtonElement>('dl-zip');
    btn.disabled = true;
    const state = $('export-state');
    try {
      const input = exportInputs();
      const summary = summarise(input);
      const name = `${conceptDirName(input.options.repeats, input.options.concept)}-dataset.zip`;

      let sink: ReturnType<typeof memorySink> | ReturnType<typeof fileSystemSink>;
      let memory: ReturnType<typeof memorySink> | null = null;
      const picker = (window as Window & { showSaveFilePicker?: (o: unknown) => Promise<FileSystemFileHandle> })
        .showSaveFilePicker;
      if (picker) {
        const handle = await picker({
          suggestedName: name,
          types: [{ description: 'ZIP archive', accept: { 'application/zip': ['.zip'] } }],
        });
        sink = fileSystemSink(await handle.createWritable());
        log.emit('info', 'Streaming the archive straight to disk.');
      } else {
        memory = memorySink(1_500_000_000);
        sink = memory;
        log.emit('info', 'This browser has no save picker, so the archive is built in memory first.');
      }

      state.replaceChildren(el('p', { class: 'panel-note' }, ['Packing…']));
      await writeZip(sink, exportEntries(input, summary), {
        entryCount: summary.entryCount,
        payloadBytes: summary.payloadBytes,
        onProgress: (done, total, written) => {
          state.replaceChildren(
            el('p', { class: 'panel-note' }, [
              `Packing ${formatCount(done)} of ${formatCount(total)} — ${formatBytes(written)} written.`,
            ]),
          );
        },
      });

      if (memory) {
        const blob = memory.blob();
        download(blob, name);
        state.replaceChildren(el('p', { class: 'panel-note' }, [`${name} — ${formatBytes(blob.size)}.`]));
        offerShare(blob, name);
      } else {
        state.replaceChildren(el('p', { class: 'panel-note' }, [`Saved ${name}.`]));
      }
      log.emit('good', `Exported ${formatCount(kept.length)} image(s).`);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        state.replaceChildren();
      } else if (err instanceof ZipTooBigError) {
        fail(err.message);
      } else {
        fail(err instanceof Error ? err.message : String(err));
      }
    } finally {
      btn.disabled = false;
    }
  })();
});

$('dl-report').addEventListener('click', () => {
  if (!analysis) return;
  const input = exportInputs();
  const plan = planExport(
    kept.map((id) => analysis!.records.find((r) => r.id === id)!),
    input.options,
  );
  let csv = CSV_BOM;
  for (const row of reportCsv({ records: analysis.records, decisions, groups, plan })) csv += row;
  download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'dedup-report.csv');
  log.emit('good', 'dedup-report.csv saved.');
});

function download(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

function offerShare(blob: Blob, name: string): void {
  const btn = $<HTMLButtonElement>('share-zip');
  const file = new File([blob], name, { type: 'application/zip' });
  if (!navigator.canShare?.({ files: [file] })) return;
  btn.removeAttribute('hidden');
  btn.onclick = () => {
    void navigator.share({ files: [file], title: name }).catch(() => {
      /* the user dismissing the share sheet is not an error */
    });
  };
}

/* ── Keyboard ──────────────────────────────────────────────────────────────── */

document.addEventListener('keydown', (e) => {
  const t = e.target as HTMLElement;
  if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable) return;
  if (e.metaKey || e.ctrlKey || e.altKey) return;
  const k = e.key.toLowerCase();
  if (k === 'l') drawer.isOpen ? drawer.close() : drawer.open();
  else if (k === 'e' && !$('review-panel').hasAttribute('hidden')) {
    renderTree();
    show('export');
  } else if (e.key === '?') openModal('keys');
});

$('log-copy').addEventListener('click', () => {
  void navigator.clipboard.writeText(log.asText()).then(
    () => toast('Event log copied.'),
    () => toast('The browser would not give access to the clipboard.'),
  );
});

$('retry').addEventListener('click', () => {
  $('error').setAttribute('hidden', '');
});

log.emit('info', `Loraprep ready. Model: ${MODEL_ID}, downloaded on first use.`);
